import { TableCollection } from '@react-types/table';
import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';

type NormalAlignment = 'start' | 'center' | 'end';

export interface TableConfig {
    rowsPerPage: number;
    currentPage: number;
    color?: string;
    animated?: boolean;
    footerAlign?: NormalAlignment;
    collection?: TableCollection<any>;
    rowClassName?: (item: any) => string | undefined;
    setFooterAlign?: (value: NormalAlignment) => void;
    setAnimated?: (value: boolean) => void;
    setCollection?: (value: TableCollection<any>) => void;
    setRowsPerPage?: (value: number) => void;
    setCurrentPage?: (value: number) => void;
    setColor?: (value: string) => void;
}

const defaultContext = {
    footerAlign: 'end' as NormalAlignment,
    rowsPerPage: 0,
    currentPage: 1,
    animated: true
};

export const TableContext = createContext<TableConfig>(defaultContext);

export const useTableContext = (): TableConfig => useContext<TableConfig>(TableContext);

export type ProviderProps = {
    children: ReactNode;
    defaultValues?: Partial<TableConfig>;
};

const Provider: FC<React.PropsWithChildren<React.PropsWithChildren<ProviderProps>>> = ({ children, defaultValues }) => {
    const [footerAlign, setFooterAlign] = useState(defaultValues?.footerAlign || defaultContext.footerAlign);
    const [animated, setAnimated] = useState(
        defaultValues?.animated === undefined ? defaultContext.animated : defaultValues?.animated
    );
    const [collection, setCollection] = useState(defaultValues?.collection);
    const [rowsPerPage, setRowsPerPage] = useState(
        defaultValues?.rowsPerPage === undefined ? defaultContext.rowsPerPage : defaultValues?.rowsPerPage
    );
    const [currentPage, setCurrentPage] = useState(defaultContext.currentPage);
    const [color, setColor] = useState<string>(defaultValues?.color);

    const providerValue = useMemo<TableConfig>(
        () => ({
            animated,
            footerAlign,
            collection,
            rowsPerPage,
            currentPage,
            color,
            rowClassName: defaultValues?.rowClassName,
            setFooterAlign,
            setCollection,
            setAnimated,
            setRowsPerPage,
            setColor,
            setCurrentPage
        }),
        [animated, color, collection, footerAlign, rowsPerPage, currentPage, defaultValues?.rowClassName]
    );

    return <TableContext.Provider value={providerValue}>{children}</TableContext.Provider>;
};

const Context = { Provider, Consumer: TableContext.Consumer };

export default Context;
